import { ReactNode } from 'react'

import { Head, usePage } from '@inertiajs/react'
import { Toaster } from '@/components/ui/sonner'
import { PageProps } from '@/types'

interface AuthLayoutProps {
  children: ReactNode
  title?: string
  heading: string
  description?: string
}

export function AuthLayout({ children, title, heading, description }: AuthLayoutProps) {
  const { flash } = usePage<PageProps>().props

  return (
    <>
      <Head title={title || 'Sidebrief'} />

      <div className="flex min-h-svh font-display">
        {/* Branded left panel */}
        <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] shrink-0 relative overflow-hidden flex-col justify-between p-10 bg-[#0a0f1a]">
          {/* Ambient glow */}
          <div className="absolute inset-0">
            <div className="auth-glow-1 absolute w-[500px] h-[500px] rounded-full bg-blue-500/[0.07] blur-[100px] -top-20 -right-40" />
            <div className="auth-glow-2 absolute w-[400px] h-[400px] rounded-full bg-cyan-500/[0.05] blur-[100px] bottom-0 -left-20" />
            <div className="auth-glow-3 absolute w-[300px] h-[300px] rounded-full bg-blue-500/[0.04] blur-[80px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>

          {/* Diagonal line texture */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 14px,
                rgba(255,255,255,0.5) 14px,
                rgba(255,255,255,0.5) 15px
              )`,
            }}
          />

          {/* Fine noise grain overlay */}
          <div
            className="absolute inset-0 opacity-[0.25] mix-blend-soft-light pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
              backgroundSize: '128px 128px',
            }}
          />

          {/* Top: Logo */}
          <div className="relative z-10 auth-fade-in" style={{ animationDelay: '0ms' }}>
            <img src="/sidebrief-logo.png" alt="Sidebrief" className="h-20 w-auto" />
          </div>

          {/* Middle: editorial content */}
          <div className="relative z-10 space-y-10">
            <div className="auth-fade-in" style={{ animationDelay: '80ms' }}>
<h2 className="text-[2.5rem] xl:text-[2.75rem] font-extrabold tracking-[-0.03em] leading-[1.1] text-white">
                Start your
                <br />
                business
                <br />
                <span className="text-blue-400">anywhere in Africa.</span>
              </h2>
            </div>

            <div className="auth-fade-in" style={{ animationDelay: '160ms' }}>
              <p className="text-[15px] leading-relaxed text-slate-400 max-w-[340px]">
                Incorporate and scale your company across Africa.
              </p>
            </div>

            {/* Stats row */}
            <div className="auth-fade-in flex gap-8" style={{ animationDelay: '240ms' }}>
              {[
                { value: '54', label: 'Countries' },
                { value: '5-7d', label: 'Avg. incorporation' },
                { value: '52%', label: 'Cheaper vs. advisors' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-white tracking-tight">{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="relative z-10 auth-fade-in" style={{ animationDelay: '320ms' }}>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span>&copy; {new Date().getFullYear()} Sidebrief</span>
              <span className="w-1 h-1 rounded-full bg-slate-700" />
              <span>Secure & encrypted</span>
            </div>
          </div>
        </div>

        {/* Right panel - form area */}
        <div className="flex-1 flex flex-col relative">
          {/* Subtle warm texture on right side */}
          <div className="absolute inset-0 bg-[#fafaf9]" />
          <div
            className="absolute inset-0 opacity-[0.3] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px',
            }}
          />

          {/* Mobile header */}
          <div className="lg:hidden relative z-10 flex items-center gap-2.5 px-5 py-4 border-b border-black/[0.04]">
            <img src="/sidebrief-logo.png" alt="Sidebrief" className="h-16 w-auto" />
          </div>

          {/* Form content */}
          <div className="relative z-10 flex-1 flex items-center justify-center p-6 sm:p-10">
            <div className="w-full max-w-[380px]">
              {/* Flash messages */}
              {flash.notice && (
                <div className="auth-fade-in mb-6 py-2.5 px-4 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-200/60">
                  {flash.notice}
                </div>
              )}
              {flash.alert && (
                <div className="auth-fade-in mb-6 py-2.5 px-4 bg-red-50 text-red-700 text-sm font-medium rounded-lg border border-red-200/60">
                  {flash.alert}
                </div>
              )}

              {/* Heading */}
              <div className="auth-fade-in mb-8" style={{ animationDelay: '60ms' }}>
                <h1 className="text-[1.625rem] font-extrabold tracking-[-0.02em] text-[#0a0f1a]">
                  {heading}
                </h1>
                {description && (
                  <p className="mt-2 text-sm leading-relaxed text-[#0a0f1a]/50">
                    {description}
                  </p>
                )}
              </div>

              <div className="auth-fade-in" style={{ animationDelay: '120ms' }}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toaster />
    </>
  )
}
